<template>
  <div class="modal-backdrop fade show" v-if="modelValue" @click.self="close"></div>
  <div class="modal d-block" tabindex="-1" v-if="modelValue">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content topup-modal">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-wallet2 me-2"></i>
            {{ $t('subscription.topup_balance') }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Current Balance -->
          <div class="current-balance-card">
            <span class="balance-label">{{ $t('subscription.current_balance') }}</span>
            <span class="balance-value">{{ formattedBalance }} {{ $t('subscription.currency') }}</span>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-muted">{{ $t('subscription.loading') }}</p>
          </div>

          <!-- Options Grid -->
          <div v-else class="topup-options">
            <div
              v-for="option in topupOptions"
              :key="option.key"
              class="topup-option"
              :class="{ 'has-bonus': option.bonus > 0 }"
              @click="selectOption(option)"
            >
              <div class="option-amount">{{ option.amount }} ₽</div>
              <div class="option-bonus" v-if="option.bonus > 0">
                +{{ option.bonus }} ₽ {{ $t('subscription.bonus') }}
              </div>
              <div class="option-total" v-if="option.bonus > 0">
                = {{ option.total }} ₽
              </div>
              <a :href="option.payment_url" target="_blank" class="btn btn-primary btn-sm mt-2" @click.stop>
                {{ $t('subscription.pay') }}
              </a>
            </div>
          </div>

          <!-- Info -->
          <div class="topup-info mt-3">
            <i class="bi bi-info-circle me-1"></i>
            {{ $t('subscription.topup_info') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'TopupModal',

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    chatbotName: {
      type: String,
      default: 'meetscribe'
    }
  },

  emits: ['update:modelValue'],

  computed: {
    ...mapGetters('subscription', ['getBalance', 'getTopupOptions', 'isLoading']),

    loading() {
      return this.isLoading;
    },

    topupOptions() {
      return this.getTopupOptions;
    },

    formattedBalance() {
      const balance = this.getBalance || 0;
      return balance.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadOptions();
      }
    }
  },

  methods: {
    ...mapActions('subscription', ['fetchTopupOptions']),

    close() {
      this.$emit('update:modelValue', false);
    },

    loadOptions() {
      this.fetchTopupOptions(this.chatbotName);
    },

    selectOption(option) {
      // Open payment URL
      window.open(option.payment_url, '_blank');
    }
  }
};
</script>

<style scoped>
.topup-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  border-bottom: none;
}

.modal-header .btn-close {
  filter: brightness(0) invert(1);
}

.modal-title {
  font-weight: 600;
}

/* Current Balance */
.current-balance-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: var(--yt-bg-secondary, #f9fafb);
  border-radius: 12px;
  margin-bottom: 20px;
}

.balance-label {
  color: var(--yt-text-secondary, #6b7280);
  font-size: 14px;
}

.balance-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--yt-text-primary, #1f2937);
}

/* Options Grid */
.topup-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.topup-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background-color: var(--yt-bg-card, #fff);
  border: 2px solid var(--yt-border-color, #e5e7eb);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.topup-option:hover {
  border-color: var(--yt-primary, #4F46E5);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.15);
}

.topup-option.has-bonus {
  border-color: #10B981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%);
}

.topup-option.has-bonus:hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.option-amount {
  font-size: 20px;
  font-weight: 700;
  color: var(--yt-text-primary, #1f2937);
}

.option-bonus {
  font-size: 13px;
  color: #10B981;
  font-weight: 600;
  margin-top: 4px;
}

.option-total {
  font-size: 14px;
  color: var(--yt-text-secondary, #6b7280);
  margin-top: 2px;
}

/* Info */
.topup-info {
  font-size: 13px;
  color: var(--yt-text-muted, #9ca3af);
  text-align: center;
  padding: 12px;
  background-color: var(--yt-bg-secondary, #f9fafb);
  border-radius: 8px;
}

/* Dark mode */
html[data-bs-theme="dark"] .topup-modal {
  background-color: var(--yt-bg-card, #1f2937);
  border: 1px solid var(--yt-border-color, #374151);
}

html[data-bs-theme="dark"] .modal-body {
  background-color: var(--yt-bg-card, #1f2937);
}

html[data-bs-theme="dark"] .current-balance-card,
html[data-bs-theme="dark"] .topup-info {
  background-color: var(--yt-bg-secondary, #374151);
}

html[data-bs-theme="dark"] .topup-option {
  background-color: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4B5563);
}

html[data-bs-theme="dark"] .topup-option:hover {
  border-color: var(--yt-primary, #6366F1);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
}

html[data-bs-theme="dark"] .topup-option.has-bonus {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
  border-color: #10B981;
}

html[data-bs-theme="dark"] .balance-value,
html[data-bs-theme="dark"] .option-amount {
  color: var(--yt-text-primary, #F9FAFB);
}

html[data-bs-theme="dark"] .balance-label {
  color: var(--yt-text-muted, #9CA3AF);
}

/* Responsive */
@media (max-width: 480px) {
  .topup-options {
    grid-template-columns: 1fr;
  }
}
</style>
